import { ColorPicker, Select, Alert, Card } from '@dreamtree-org/twreact-ui';
import { Check } from 'lucide-react';
import { THEME_PRESETS, FONT_PAIRS, RADII, validateTheme } from '@dreamtree-org/landing-kit/schema';
import ImageField from './ImageField';
import { toHex } from '../../lib/appearance';

const FONT_OPTIONS = Object.entries(FONT_PAIRS).map(([value, f]) => ({ value, label: f.label }));
const RADIUS_OPTIONS = [
  { value: 'none', label: 'Square' }, { value: 'sm', label: 'Slightly rounded' }, { value: 'md', label: 'Rounded' },
  { value: 'lg', label: 'More rounded' }, { value: 'full', label: 'Pill' },
].filter((o) => RADII[o.value]);
const MODE_OPTIONS = [{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }, { value: 'auto', label: "Follow the shopper's device" }];

/** Brand kit (D26): presets, logo, favicon, colours with contrast warnings, font, radius, mode. */
export default function ThemePanel({ theme, onChange, draftId, errors = [] }) {
  const { warnings } = validateTheme(theme);
  const set = (patch) => onChange({ ...theme, ...patch });
  const setColor = (key) => (raw) => {
    const hex = toHex(raw);
    if (hex) set({ [key]: hex, preset: 'custom' });
  };
  const err = (key) => (errors.find((e) => e.path === `theme.${key}`) || {}).message;
  return (
    <div className="space-y-6">
      <section className="space-y-2" aria-labelledby="presets-h">
        <h3 id="presets-h" className="text-sm font-semibold">Start from a preset</h3>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {Object.entries(THEME_PRESETS).map(([key, p]) => {
            const active = theme.preset === key;
            return (
              <button key={key} type="button" aria-pressed={active} onClick={() => set({ preset: key, primary: p.primary, accent: p.accent, font: p.font, radius: p.radius, mode: p.mode })}
                className={`rounded-lg border p-2 text-left transition ${active ? 'border-primary-600 ring-2 ring-primary-600/30' : 'border-[var(--app-border)] hover:border-primary-300'}`}>
                <span className="flex gap-1" aria-hidden="true">
                  <span className="h-6 flex-1 rounded" style={{ background: p.primary }} />
                  <span className="h-6 w-6 rounded" style={{ background: p.accent }} />
                </span>
                <span className="mt-1.5 flex items-center gap-1 text-xs font-medium">{active ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : null}{p.label}</span>
              </button>
            );
          })}
        </div>
      </section>

      <Card className="space-y-4 p-4">
        <ImageField label="Logo (shown in the header; a wide PNG with a transparent background works best)" value={theme.logo} onChange={(logo) => set({ logo })} draftId={draftId} kind="logo" error={err('logo')} />
        <ImageField label="Favicon (square PNG, at least 64×64)" value={theme.favicon} onChange={(favicon) => set({ favicon })} draftId={draftId} kind="favicon" alt={false} error={err('favicon')} />
      </Card>

      {/* Stacked: the editor column is narrow and the picker carries its own hex input. */}
      <div className="grid gap-4">
        <ColorPicker label="Brand colour (buttons, links)" value={theme.primary} onChange={setColor('primary')} />
        <ColorPicker label="Accent colour (offers, highlights)" value={theme.accent} onChange={setColor('accent')} />
      </div>
      {warnings.length ? (
        <Alert variant="warning" title="Some text may be hard to read">
          <ul className="list-disc pl-5">{warnings.map((w) => <li key={w.path + w.message}>{w.message}</li>)}</ul>
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <Select label="Fonts" options={FONT_OPTIONS} value={theme.font} onChange={(font) => set({ font })} closeOnSelect error={err('font')} />
        <Select label="Corners" options={RADIUS_OPTIONS} value={theme.radius} onChange={(radius) => set({ radius })} closeOnSelect error={err('radius')} />
        <Select label="Default mode" options={MODE_OPTIONS} value={theme.mode} onChange={(mode) => set({ mode })} closeOnSelect error={err('mode')} />
      </div>
    </div>
  );
}
